import { Children, isValidElement, type ReactNode } from 'react'

/** The short words a title leaves lowercase, unless one opens or closes it. */
const SMALL = new Set(['a', 'an', 'the', 'and', 'or', 'nor', 'but', 'of', 'to', 'in', 'on', 'at', 'for', 'by', 'from', 'with', 'as', 'per', 'via', 'vs'])

/**
 * A label in title case: "New sheet" reads "New Sheet", "Write a note"
 * reads "Write a Note". Only first letters rise — "QA", "API" and a
 * name's own capitals stay as written; the first and last words always
 * rise. `opening` and `closing` say whether the text starts or ends the
 * label, so a piece beside a number still knows it is mid-title.
 */
export function titleCase(text: string, opening = true, closing = true): string {
  const words = /[A-Za-z][\w'’]*/g
  const last = [...text.matchAll(words)].at(-1)?.index
  return text.replace(words, (word, offset: number) => {
    const edge = (opening && text.slice(0, offset).trim() === '') || (closing && offset === last)
    if (!edge && SMALL.has(word.toLowerCase())) return word
    return word[0]!.toUpperCase() + word.slice(1)
  })
}

/**
 * The same, for a label passed as children: the plain strings in it are
 * title-cased, anything richer (an icon, a styled span) passes untouched.
 * This is how the kit's labelled pieces apply the rule without each caller
 * remembering it.
 */
export function titleCaseNode(children: ReactNode): ReactNode {
  if (typeof children === 'string') return titleCase(children)
  if (!Array.isArray(children)) return children
  let opening = true
  const lastText = children.findLastIndex((child) => typeof child === 'string' && child.trim() !== '')
  return Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      const cased = titleCase(child, opening, index === lastText)
      if (child.trim() !== '') opening = false
      return cased
    }
    if (typeof child === 'number' || isValidElement(child)) opening = false
    return child
  })
}
